// Registry of exam prep tracks. Add new exams here as they're built.

import type { ExamMeta } from './types';

export const EXAMS: ExamMeta[] = [
  // ===== High School: SC junior-year curriculum =====
  {
    id: 'USHC',
    code: 'USHC',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'U.S. History and the Constitution',
    tagline: 'SC EOCEP-tested junior-year course covering the Constitution, Reconstruction, Gilded Age, Progressive Era, two World Wars, the New Deal, the Cold War, and Civil Rights. EOCEP counts 20% of your final course grade.',
    status: 'active',
    level: 'High School Required',
    isEOCEP: true,
    domains: [
      { label: 'Foundations & Constitution',     weight: '~10%' },
      { label: 'Reconstruction',                 weight: '~10%' },
      { label: 'Industrialization & Gilded Age', weight: '~10%' },
      { label: 'Progressive Era',                weight: '~10%' },
      { label: 'World War I & 1920s',            weight: '~10%' },
      { label: 'Great Depression & New Deal',    weight: '~10%' },
      { label: 'World War II',                   weight: '~10%' },
      { label: 'Cold War & Containment',         weight: '~15%' },
      { label: 'Civil Rights Movement',          weight: '~15%' },
    ],
    durationMin: 120,
    questionCount: 118,
  },
  {
    id: 'ALG1',
    code: 'Algebra 1',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'Algebra 1',
    tagline: 'SC EOCEP-tested 9th-grade math: real numbers, linear equations, functions, systems, polynomials, quadratics, exponentials, and statistics. EOCEP counts 20% of your final course grade.',
    status: 'active',
    level: 'High School Required',
    isEOCEP: true,
    domains: [
      { label: 'Number & Quantity',               weight: '~10%' },
      { label: 'Linear Equations & Inequalities', weight: '~15%' },
      { label: 'Functions',                       weight: '~15%' },
      { label: 'Systems of Equations',            weight: '~10%' },
      { label: 'Polynomials',                     weight: '~10%' },
      { label: 'Quadratic Functions',             weight: '~15%' },
      { label: 'Exponential Functions',           weight: '~10%' },
      { label: 'Statistics & Data',               weight: '~15%' },
    ],
    durationMin: 120,
    questionCount: 100,
  },
  {
    id: 'ENG2',
    code: 'English 2',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'English 2',
    tagline: 'SC EOCEP-tested 10th-grade ELA: reading literature and nonfiction, literary devices, argument analysis, language conventions, vocabulary, and writing revision. EOCEP counts 20% of your final course grade.',
    status: 'active',
    level: 'High School Required',
    isEOCEP: true,
    domains: [
      { label: 'Reading Literature',                  weight: '~15%' },
      { label: 'Reading Informational Text',          weight: '~15%' },
      { label: 'Literary Devices & Figurative Language', weight: '~10%' },
      { label: "Author's Craft & Structure",          weight: '~10%' },
      { label: 'Argument & Evidence',                 weight: '~15%' },
      { label: 'Language Conventions',                weight: '~15%' },
      { label: 'Vocabulary in Context',               weight: '~10%' },
      { label: 'Writing & Revision',                  weight: '~10%' },
    ],
    durationMin: 120,
    questionCount: 100,
  },
  {
    id: 'BIO1',
    code: 'Biology 1',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'Biology 1',
    tagline: 'SC EOCEP-tested 9th/10th-grade biology: cells, biochemistry, genetics, evolution, ecology, classification, homeostasis, and scientific inquiry. EOCEP counts 20% of your final course grade.',
    status: 'active',
    level: 'High School Required',
    isEOCEP: true,
    domains: [
      { label: 'Cells & Cell Processes',           weight: '~15%' },
      { label: 'Biochemistry & Energy',            weight: '~10%' },
      { label: 'Genetics & Heredity',              weight: '~15%' },
      { label: 'Evolution & Natural Selection',    weight: '~15%' },
      { label: 'Ecology & Ecosystems',             weight: '~15%' },
      { label: 'Classification & Biodiversity',    weight: '~10%' },
      { label: 'Homeostasis & Body Systems',       weight: '~10%' },
      { label: 'Scientific Inquiry & Lab Skills',  weight: '~10%' },
    ],
    durationMin: 120,
    questionCount: 100,
  },

  // ===== Non-IT: Standardized Tests =====
  {
    id: 'SCPERMIT',
    code: 'SC Permit',
    vendor: 'SC DMV',
    category: 'High School',
    title: 'South Carolina Learner\'s Permit',
    tagline: 'Pass your SC permit test on the first try! 🚗 Teen-friendly study guide + 200+ practice questions covering road signs, traffic laws, and safe driving. You need 24/30 (80%) to pass.',
    status: 'active',
    level: 'Standardized Test',
    domains: [
      { label: 'Road Signs & Signals', weight: '30%' },
      { label: 'Traffic Laws & Rules', weight: '40%' },
      { label: 'Safe Driving',         weight: '30%' },
    ],
    durationMin: 30,
    passScore: 80,
    questionCount: 119,
  },
  {
    id: 'SAT',
    code: 'SAT',
    vendor: 'College Board',
    category: 'High School',
    title: 'SAT College Readiness',
    tagline: 'Digital adaptive SAT — Reading & Writing + Math. 1000+ practice questions with adaptive testing that mirrors the real exam\'s Module 1 → Module 2 difficulty scaling. Score 400–1600.',
    status: 'active',
    level: 'Standardized Test',
    domains: [
      { label: 'Reading & Writing',  weight: '50%' },
      { label: 'Math',               weight: '50%' },
    ],
    durationMin: 134,
    passScore: undefined,
    questionCount: 1010,
  },
  // ===== High School: SC 11th-grade curriculum =====
  {
    id: 'ENG3',
    code: 'English 3',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'English 3',
    tagline: 'SC 11th-grade ELA: reading literature, informational text, argument and rhetoric, research writing, language conventions, and speaking & listening.',
    status: 'active',
    level: 'High School Required',
    domains: [
      { label: 'Reading Literature',         weight: '~20%' },
      { label: 'Reading Informational Text', weight: '~20%' },
      { label: 'Argument & Rhetoric',        weight: '~20%' },
      { label: 'Research Writing',           weight: '~15%' },
      { label: 'Language & Conventions',     weight: '~15%' },
      { label: 'Speaking & Listening',       weight: '~10%' },
    ],
    durationMin: 120,
    questionCount: 113,
  },
  {
    id: 'ALG2',
    code: 'Algebra 2',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'Algebra 2',
    tagline: 'SC 11th-grade math: polynomials, complex numbers, exponentials & logs, systems and matrices, conic sections, sequences & series, and statistics.',
    status: 'active',
    level: 'High School Required',
    domains: [
      { label: 'Polynomial & Rational Functions',    weight: '~20%' },
      { label: 'Complex Numbers',                    weight: '~10%' },
      { label: 'Exponential & Logarithmic Functions',weight: '~20%' },
      { label: 'Systems & Matrices',                 weight: '~15%' },
      { label: 'Conic Sections',                     weight: '~15%' },
      { label: 'Sequences, Series & Probability',    weight: '~20%' },
    ],
    durationMin: 120,
    questionCount: 145,
  },
  {
    id: 'PRECALC',
    code: 'Pre-Calculus',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'Pre-Calculus',
    tagline: 'SC 11th-grade math: functions and transformations, polynomial and rational functions, exponentials, trigonometry, analytic trigonometry, vectors, and sequences & series.',
    status: 'active',
    level: 'High School Required',
    domains: [
      { label: 'Functions & Transformations',         weight: '~15%' },
      { label: 'Polynomial & Rational Functions',     weight: '~15%' },
      { label: 'Exponential & Logarithmic Functions', weight: '~15%' },
      { label: 'Trigonometric Functions',             weight: '~15%' },
      { label: 'Analytic Trigonometry',               weight: '~15%' },
      { label: 'Vectors & Parametrics',               weight: '~10%' },
      { label: 'Sequences & Series',                  weight: '~15%' },
    ],
    durationMin: 120,
    questionCount: 147,
  },
  {
    id: 'PROBSTAT',
    code: 'Probability & Statistics',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'Probability & Statistics',
    tagline: 'SC 11th-grade math: data collection and design, descriptive statistics, probability rules, distributions, the Central Limit Theorem, confidence intervals, and hypothesis testing.',
    status: 'active',
    level: 'High School Required',
    domains: [
      { label: 'Data Collection & Design',           weight: '~15%' },
      { label: 'Descriptive Statistics',             weight: '~15%' },
      { label: 'Probability',                        weight: '~15%' },
      { label: 'Probability Distributions',          weight: '~15%' },
      { label: 'Sampling Distributions',             weight: '~10%' },
      { label: 'Confidence Intervals',               weight: '~15%' },
      { label: 'Hypothesis Testing & Bivariate Data',weight: '~15%' },
    ],
    durationMin: 120,
    questionCount: 144,
  },
  {
    id: 'CHEM',
    code: 'Chemistry',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'Chemistry',
    tagline: 'SC 11th-grade chemistry: matter and measurement, atomic structure, periodic trends, chemical bonding, stoichiometry, states of matter, thermodynamics, kinetics, equilibrium, and acids & bases.',
    status: 'active',
    level: 'High School Required',
    domains: [
      { label: 'Matter & Measurement',                       weight: '~15%' },
      { label: 'Atomic Structure',                           weight: '~15%' },
      { label: 'Periodic Table',                             weight: '~10%' },
      { label: 'Chemical Bonding',                           weight: '~15%' },
      { label: 'Stoichiometry',                              weight: '~15%' },
      { label: 'States of Matter & Thermodynamics',          weight: '~15%' },
      { label: 'Reaction Kinetics, Equilibrium & Acids/Bases',weight: '~15%' },
    ],
    durationMin: 120,
    questionCount: 144,
  },
  {
    id: 'PHYS',
    code: 'Physics',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'Physics',
    tagline: 'SC 11th-grade physics: kinematics, energy and momentum, waves and sound, light and optics, electricity and circuits, and magnetism.',
    status: 'active',
    level: 'High School Required',
    domains: [
      { label: 'Kinematics',              weight: '~20%' },
      { label: 'Energy & Momentum',       weight: '~20%' },
      { label: 'Waves & Sound',           weight: '~15%' },
      { label: 'Light & Optics',          weight: '~15%' },
      { label: 'Electricity & Circuits',  weight: '~15%' },
      { label: 'Magnetism',               weight: '~15%' },
    ],
    durationMin: 120,
    questionCount: 153,
  },
  {
    id: 'ENVSCI',
    code: 'Environmental Science',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'Environmental Science',
    tagline: 'SC 11th-grade environmental science: Earth systems, biogeochemical cycles, ecology, biodiversity, human population, climate change, water resources, and energy sustainability.',
    status: 'active',
    level: 'High School Required',
    domains: [
      { label: 'Earth Systems & Biogeochemical Cycles', weight: '~15%' },
      { label: 'Ecology & Ecosystems',                  weight: '~15%' },
      { label: 'Biodiversity & Conservation',           weight: '~15%' },
      { label: 'Human Population & Land Use',           weight: '~10%' },
      { label: 'Climate Change & Atmosphere',           weight: '~15%' },
      { label: 'Water Resources',                       weight: '~15%' },
      { label: 'Energy Resources & Sustainability',     weight: '~15%' },
    ],
    durationMin: 120,
    questionCount: 117,
  },
  {
    id: 'PFIN',
    code: 'Personal Finance',
    vendor: 'SC Department of Education',
    category: 'High School',
    title: 'Personal Finance',
    tagline: 'SC 11th-grade personal finance: earning income, budgeting, saving and banking, credit and debt, investing, insurance, and taxes — real skills for real life.',
    status: 'active',
    level: 'High School Required',
    domains: [
      { label: 'Earning Income',              weight: '~15%' },
      { label: 'Budgeting & Spending',        weight: '~15%' },
      { label: 'Saving & Banking',            weight: '~15%' },
      { label: 'Credit & Debt',               weight: '~15%' },
      { label: 'Investing & Markets',         weight: '~15%' },
      { label: 'Insurance & Risk Management', weight: '~10%' },
      { label: 'Taxes & Government Programs', weight: '~15%' },
    ],
    durationMin: 120,
    questionCount: 137,
  },
];

export const findExam = (id: string): ExamMeta | undefined => EXAMS.find(e => e.id === id);
